import { Box } from "@mui/material";
import ResizableSplitPane from "@/ai/ResizableSplitPane";

import CanvasPanel from "@/ai/panels/CanvasPanel";
import ActionsPanel from "@/ai/panels/ActionsPanel";
import LinksPanel from "@/ai/panels/LinksPanel";
import BidPreviewPanel from "@/ai/panels/BidPreviewPanel";
import WizardPanel from "@/ai/panels/WizardPanel";

/**
 * Panel component registry — maps panel IDs from workflowRegistry to React components.
 */
const PANEL_COMPONENTS = {
    canvas: CanvasPanel,
    actions: ActionsPanel,
    links: LinksPanel,
    bidPreview: BidPreviewPanel,
    wizard: WizardPanel,
};

/**
 * WidgetZone — dynamically composes the right-hand widget area
 * based on the current workflow's `panels` configuration.
 *
 * When the user switches threads (changing agent_type), this component
 * automatically recomposes to show the correct panels.
 *
 * Props:
 *  - workflowConfig: object from WORKFLOW_REGISTRY (includes panels[])
 *  - panelProps: Record<panelId, props> — props to pass to each panel component
 */
export default function WidgetZone({ workflowConfig, panelProps = {} }) {
    if (!workflowConfig || workflowConfig.panels.length === 0) {
        return null;
    }

    const panelIds = workflowConfig.panels;
    const renderedPanels = panelIds
        .map((panelId) => {
            const PanelComponent = PANEL_COMPONENTS[panelId];
            if (!PanelComponent) return null;

            const props = panelProps[panelId] || {};
            return (
                <Box
                    key={panelId}
                    sx={{ width: "100%", height: "100%", overflow: "hidden" }}
                >
                    <PanelComponent {...props} />
                </Box>
            );
        })
        .filter(Boolean);

    if (renderedPanels.length === 0) return null;

    // Single panel: render full-size
    if (renderedPanels.length === 1) {
        return (
            <Box sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
                {renderedPanels[0]}
            </Box>
        );
    }

    // Two panels: render in a vertical split pane
    if (renderedPanels.length === 2) {
        return (
            <ResizableSplitPane
                orientation="vertical"
                initialSplit={0.65}
                minSizePx={80}
            >
                {renderedPanels[0]}
                {renderedPanels[1]}
            </ResizableSplitPane>
        );
    }

    // Three+ panels: first takes 60%, rest share remaining space equally
    return (
        <ResizableSplitPane
            orientation="vertical"
            initialSplit={0.6}
            minSizePx={60}
        >
            {renderedPanels[0]}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    height: "100%",
                    overflow: "auto",
                }}
            >
                {renderedPanels.slice(1)}
            </Box>
        </ResizableSplitPane>
    );
}
